import { observer } from "mobx-react";
import React, { useContext } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  Badge,
  List,
  ThemeIcon,
  Alert,
  Group,
  Button,
  Box,
  Divider,
  Image,
  Anchor,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconBolt,
  IconCurrencyBitcoin,
  IconExternalLink,
  IconInfoCircle,
} from "@tabler/icons-react";
import { CheckBitcoinAddressStoreContext } from "../stores/CheckBitcoinAddressStore";
import { msatsToSatsDisplay } from "../helpers/lightningAddressChecker";

const AddressResultDisplay: React.FC = observer(() => {
  const store = useContext(CheckBitcoinAddressStoreContext);

  if (!store.hasChecked || !store.addressInfo) return null;

  const info = store.addressInfo.data;
  const lnInfo = store.lightningInfo?.data;

  // For Lightning Addresses, only show as confirmed Bitcoin if the server check passed
  const isLnAddress = info.type === "lightning_address";
  // Shaped like a Bitcoin address, but the checksum (or witness version) is wrong
  const isInvalidBitcoin = info.type === "bitcoin_invalid";
  const lnVerified = isLnAddress && lnInfo?.isValid === true;
  const lnFailed = isLnAddress && lnInfo != null && !lnInfo.isValid;
  const lnPending =
    isLnAddress && (store.isCheckingLightning || lnInfo == null);

  // Override isBitcoin for Lightning Addresses: only true if server confirmed
  const effectiveIsBitcoin = isLnAddress ? lnVerified : info.isBitcoin;

  const badgeColor = isLnAddress
    ? lnVerified
      ? "green"
      : lnFailed
        ? "red"
        : "gray"
    : info.isBitcoin
      ? info.type.includes("testnet")
        ? "yellow"
        : "green"
      : info.type === "unknown"
        ? "gray"
        : "red";

  const badgeLabel = isLnAddress
    ? lnVerified
      ? "Lightning Address"
      : lnFailed
        ? "Not a Lightning Address"
        : "Checking Lightning Address…"
    : info.label;

  const badgeIcon = isLnAddress ? (
    lnVerified ? (
      <IconBolt size={14} />
    ) : lnFailed ? (
      <IconX size={14} />
    ) : (
      <IconBolt size={14} />
    )
  ) : effectiveIsBitcoin ? (
    <IconCurrencyBitcoin size={14} />
  ) : info.type === "unknown" ? (
    <IconAlertTriangle size={14} />
  ) : (
    <IconX size={14} />
  );

  const borderColor = isLnAddress
    ? lnVerified
      ? "rgba(34, 197, 94, 0.3)"
      : lnFailed
        ? "rgba(239, 68, 68, 0.3)"
        : "rgba(255, 255, 255, 0.1)"
    : effectiveIsBitcoin
      ? "rgba(34, 197, 94, 0.3)"
      : info.type === "unknown"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(239, 68, 68, 0.3)";

  return (
    <Stack gap="md">
      {/* Main result card */}
      <Paper
        p="lg"
        radius="md"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          border: `1px solid ${borderColor}`,
        }}
      >
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <div>
              <Badge
                size="lg"
                color={badgeColor}
                variant="light"
                leftSection={badgeIcon}
                mb="xs"
              >
                {badgeLabel}
              </Badge>
              <Title order={4} c="white">
                {isLnAddress
                  ? lnVerified
                    ? "Valid Lightning Address"
                    : lnFailed
                      ? "Not a Valid Lightning Address"
                      : "Checking Lightning Address…"
                  : effectiveIsBitcoin
                    ? "Bitcoin / Lightning Network"
                    : info.type === "unknown"
                      ? "Unknown Format"
                      : isInvalidBitcoin
                        ? "Invalid Bitcoin Address"
                        : "Not a Bitcoin Address"}
              </Title>
            </div>
            {effectiveIsBitcoin && !isLnAddress && (
              <ThemeIcon color="green" size="lg" radius="xl" variant="light">
                <IconCheck size={20} />
              </ThemeIcon>
            )}
            {lnVerified && (
              <ThemeIcon color="green" size="lg" radius="xl" variant="light">
                <IconCheck size={20} />
              </ThemeIcon>
            )}
            {lnFailed && (
              <ThemeIcon color="red" size="lg" radius="xl" variant="light">
                <IconX size={20} />
              </ThemeIcon>
            )}
            {!effectiveIsBitcoin && !isLnAddress && info.type !== "unknown" && (
              <ThemeIcon color="red" size="lg" radius="xl" variant="light">
                <IconX size={20} />
              </ThemeIcon>
            )}
          </Group>

          <Text c="dimmed" size="sm">
            {info.description}
          </Text>

          {info.details.length > 0 && (
            <>
              <Divider color="rgba(255,255,255,0.1)" my="xs" />
              <List
                spacing="xs"
                size="sm"
                c="dimmed"
                icon={
                  <ThemeIcon
                    color={effectiveIsBitcoin ? "teal" : "gray"}
                    size={18}
                    radius="xl"
                    variant="light"
                  >
                    <IconInfoCircle size={12} />
                  </ThemeIcon>
                }
              >
                {info.details.map((detail, i) => (
                  <List.Item key={i}>{detail}</List.Item>
                ))}
              </List>
            </>
          )}
        </Stack>
      </Paper>

      {/* Lightning Address server check results */}
      {info.type === "lightning_address" && (
        <Paper
          p="lg"
          radius="md"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(255, 200, 0, 0.2)",
          }}
        >
          <Group gap="xs" mb="sm">
            <IconBolt size={20} color="#fbbf24" />
            <Title order={5} c="white">
              Lightning Address Server Check
            </Title>
          </Group>

          {store.isCheckingLightning && (
            <Text c="dimmed" size="sm">
              Querying {store.addressInput.split("@")[1]}...
            </Text>
          )}

          {lnInfo && !store.isCheckingLightning && (
            <Stack gap="sm">
              {lnInfo.isValid ? (
                <>
                  <Alert
                    icon={<IconCheck size={16} />}
                    color="green"
                    variant="light"
                    title="Active Lightning Address"
                  >
                    The server at {lnInfo.domain} confirmed this Lightning
                    Address is active and ready to receive payments.
                  </Alert>

                  {lnInfo.thumbnail && (
                    <Group justify="center">
                      <Image
                        src={lnInfo.thumbnail}
                        alt="Lightning Address avatar"
                        w={80}
                        h={80}
                        radius="md"
                        style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                      />
                    </Group>
                  )}

                  {lnInfo.description && (
                    <Box>
                      <Text size="xs" c="dimmed" fw={600} mb={2}>
                        Description
                      </Text>
                      <Text size="sm" c="white">
                        {lnInfo.description}
                      </Text>
                    </Box>
                  )}

                  <Group grow>
                    <Box>
                      <Text size="xs" c="dimmed" fw={600} mb={2}>
                        Min Sendable
                      </Text>
                      <Text size="sm" c="white" ff="monospace">
                        {lnInfo.minSendable != null
                          ? msatsToSatsDisplay(lnInfo.minSendable)
                          : "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed" fw={600} mb={2}>
                        Max Sendable
                      </Text>
                      <Text size="sm" c="white" ff="monospace">
                        {lnInfo.maxSendable != null
                          ? msatsToSatsDisplay(lnInfo.maxSendable)
                          : "—"}
                      </Text>
                    </Box>
                    {lnInfo.commentAllowed != null &&
                      lnInfo.commentAllowed > 0 && (
                        <Box>
                          <Text size="xs" c="dimmed" fw={600} mb={2}>
                            Comment Allowed
                          </Text>
                          <Text size="sm" c="white" ff="monospace">
                            {lnInfo.commentAllowed} chars
                          </Text>
                        </Box>
                      )}
                  </Group>
                </>
              ) : (
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  color="yellow"
                  variant="light"
                  title="Could Not Verify"
                >
                  {lnInfo.error}
                </Alert>
              )}
              <Text size="sm" c="dimmed">
                Full test (CORS, a live invoice, its amount and description
                hash):{" "}
                <Anchor
                  href={`/lightning-address?address=${encodeURIComponent(store.addressInput.trim())}`}
                  c="orange.4"
                  size="sm"
                >
                  open the Lightning Address tester →
                </Anchor>
              </Text>
            </Stack>
          )}
        </Paper>
      )}

      {/* BOLT11 — link to decoder */}
      {info.type === "bolt11_invoice" && (
        <Paper
          p="lg"
          radius="md"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(34, 197, 94, 0.2)",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs">
              <IconBolt size={20} color="#22c55e" />
              <Title order={5} c="white">
                Decode This Invoice
              </Title>
            </Group>
            <Text c="dimmed" size="sm">
              Use the Rizful BOLT11 Invoice Decoder to see the full details of
              this invoice — amount, destination node, expiry, route hints, and
              more.
            </Text>
            <Button
              component="a"
              href={`/decode_invoice?invoice=${encodeURIComponent(
                store.addressInput.trim(),
              )}`}
              variant="light"
              color="teal"
              leftSection={<IconExternalLink size={16} />}
            >
              Open in Invoice Decoder
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Non-Bitcoin — recommend Rizful */}
      {!info.isBitcoin && info.type !== "unknown" && !isInvalidBitcoin && (
        <Paper
          p="lg"
          radius="md"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(251, 146, 60, 0.25)",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs">
              <IconCurrencyBitcoin size={20} color="#f97316" />
              <Title order={5} c="white">
                Need a Real Bitcoin Address?
              </Title>
            </Group>
            <Text c="dimmed" size="sm">
              The address you entered is for{" "}
              {info.label.replace(" Address", "").replace(" (Probable)", "")},
              which is not Bitcoin. If you need a proper Bitcoin on-chain
              address and a Lightning Address to send and receive Bitcoin
              instantly, sign up for a free account at Rizful.
            </Text>
            <Button
              component="a"
              href="https://rizful.com/"
              variant="light"
              color="orange"
              leftSection={<IconBolt size={16} />}
            >
              Get Bitcoin & Lightning Addresses on Rizful
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Unknown — recommend Rizful */}
      {info.type === "unknown" && (
        <Paper
          p="lg"
          radius="md"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(251, 146, 60, 0.25)",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs">
              <IconCurrencyBitcoin size={20} color="#f97316" />
              <Title order={5} c="white">
                Get a Bitcoin & Lightning Address
              </Title>
            </Group>
            <Text c="dimmed" size="sm">
              Not sure what kind of address you need? Sign up at Rizful to get a
              free Bitcoin on-chain address and a Lightning Address — the
              fastest, cheapest way to send and receive Bitcoin.
            </Text>
            <Button
              component="a"
              href="https://rizful.com/"
              variant="light"
              color="orange"
              leftSection={<IconBolt size={16} />}
            >
              Sign Up at Rizful
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
});

export default AddressResultDisplay;
